<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <div class="text-subtitle-2 font-weight-bold">{{ $t('documents.operationsTab') }}</div>
        <div class="caption grey--text">{{ $t('documents.opsSummary') }}</div>
      </div>

      <div class="d-flex align-center">
        <v-chip small color="primaryTransp" text-color="primary" class="font-weight-bold mr-2">
          {{ operations.length }} {{ $t(operations.length === 1 ? 'documents.operationAlias' : 'documents.operations').toLowerCase() }}
        </v-chip>
        <v-chip small outlined class="font-weight-medium mr-3">
          {{ documentCount }} {{ $t(documentCount === 1 ? 'documents.document' : 'documents.documents').toLowerCase() }}
        </v-chip>
        <v-btn
          v-if="documentCount"
          small
          outlined
          color="primary"
          class="zip-btn text-none font-weight-medium"
          :loading="zipping"
          :disabled="zipping"
          data-testid="documents-download-archive"
          @click="$emit('download-zip')"
        >
          <v-icon small left>mdi-folder-zip</v-icon>
          {{ $t('documents.downloadAll') }}
        </v-btn>
      </div>
    </div>

    <!-- Operations Accordion List -->
    <v-expansion-panels v-if="operations.length" multiple accordion class="documents-operations-panels">
      <v-expansion-panel
        v-for="operation in operations"
        :key="operation.operation_id"
        class="documents-operation-panel mb-2"
      >
        <v-expansion-panel-header class="py-3 px-4 operation-panel-header" data-testid="documents-operation-header">
          <div class="d-flex align-center justify-space-between w-100 pr-2 flex-nowrap overflow-hidden">
            <!-- Left: Operation Name/Alias on top, Chips stacked below -->
            <div class="d-flex flex-column mr-3 min-w-0 flex-grow-1">
              <div class="d-flex align-center mb-1">
                <v-icon small color="primary" class="mr-2 flex-shrink-0">mdi-swap-horizontal</v-icon>
                <span
                  class="font-weight-bold text-subtitle-2 text-truncate operation-alias-text"
                  :title="operation.alias || operation.operation_id"
                >
                  {{ operation.alias || operation.operation_id }}
                </span>
              </div>

              <!-- Chips stacked below the operation name -->
              <div class="d-flex align-center chips-row pl-6">
                <!-- AML check badge (Checked vs Pending) -->
                <v-chip
                  v-if="isAmlApproved(operation)"
                  x-small
                  color="aliveTransp"
                  text-color="alive"
                  class="mr-2 font-weight-medium flex-shrink-0 aml-chip"
                >
                  <v-icon x-small left color="alive">mdi-shield-check</v-icon>
                  {{ $t('documents.amlChecked') }}
                </v-chip>
                <v-chip
                  v-else
                  x-small
                  color="yellowTransp"
                  text-color="orangeColor"
                  class="mr-2 font-weight-medium flex-shrink-0 aml-chip"
                >
                  <v-icon x-small left color="orangeColor">mdi-alert</v-icon>
                  {{ $t('documents.amlPending') }}
                </v-chip>

                <!-- Document count badge -->
                <v-chip x-small outlined class="font-weight-medium flex-shrink-0 doc-count-chip">
                  {{ (operation.documents || []).length }} {{ $t((operation.documents || []).length === 1 ? 'documents.document' : 'documents.documents').toLowerCase() }}
                </v-chip>
              </div>
            </div>

            <!-- Right: Timestamp -->
            <div class="d-flex align-center flex-shrink-0 ml-auto right-meta-col">
              <div class="date-col text-right d-none d-sm-inline-flex justify-end">
                <date-time-layout
                  v-if="operation.last_upload || operation.timecreate"
                  :timestamp="operation.last_upload || operation.timecreate"
                  class="caption grey--text text-no-wrap"
                />
              </div>
            </div>
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content class="px-2 pt-2 pb-1">
          <!-- Operation ID displayed in expanded dropdown -->
          <div class="d-flex align-center mb-3 px-1">
            <copyable-id
              :id="operation.operation_id"
              type="Operation"
              :number-of-charts="100"
              class="caption grey--text text--lighten-1"
            />
          </div>

          <v-card v-if="(operation.documents || []).length" class="documents-tab-card" outlined>
            <v-simple-table dense class="documents-tab-table">
              <thead>
                <tr>
                  <th style="width: 185px;">{{ $t('documents.slot') }}</th>
                  <th>{{ $t('documents.filename') }}</th>
                  <th style="width: 165px;">{{ $t('documents.uploaded') }}</th>
                  <th style="width: 80px;">{{ $t('documents.size') }}</th>
                  <th class="text-right" style="width: 44px;"></th>
                </tr>
              </thead>
              <tbody>
                <documents-slot-row
                  v-for="document in operation.documents"
                  :key="`${operation.operation_id}-${document.doctype}`"
                  :document="document"
                  :downloading="isDownloading(operation, document)"
                  show-slot
                  @download="$emit('download', { operation, document })"
                />
              </tbody>
            </v-simple-table>
          </v-card>

          <div v-else class="grey--text caption py-3 px-2">
            <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
            {{ $t('documents.noOperationDocuments') }}
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Empty state -->
    <v-card v-else class="empty-state-card text-center py-12" outlined>
      <v-icon size="48" color="grey darken-2">mdi-swap-horizontal</v-icon>
      <div class="grey--text text--lighten-1 mt-3 font-weight-medium">
        {{ $t('documents.noDocuments') }}
      </div>
      <div class="caption grey--text text--darken-1 mt-1">
        {{ isPro ? $t('documents.noOperationsPro') : $t('documents.noOperations') }}
      </div>
    </v-card>
  </div>
</template>

<script>
import CopyableId from '@/components/common/CopyableId.vue'
import DateTimeLayout from '@/components/common/DateTimeLayout.vue'
import DocumentsSlotRow from './DocumentsSlotRow.vue'

export default {
  name: 'DocumentsOperationsTab',

  components: { CopyableId, DateTimeLayout, DocumentsSlotRow },

  props: {
    isPro:       { type: Boolean, default: false },
    operations:  { type: Array, default: () => [] },
    // Identifies the one row currently being fetched: a slot is only unique
    // within its operation, so both halves are needed.
    downloading: { type: Object, default: null },
    zipping:     { type: Boolean, default: false },
  },

  emits: ['download', 'download-zip'],

  computed: {
    documentCount () {
      return this.operations.reduce((total, op) => total + (op.documents || []).length, 0)
    },
  },

  methods: {
    isAmlApproved (operation) {
      const val = operation.aml_check ?? operation.amlCheck
      return val === true || val === 'true' || val === 1 || val === '1'
    },
    isDownloading (operation, document) {
      return !!this.downloading &&
        this.downloading.operationId === operation.operation_id &&
        this.downloading.doctype === document.doctype
    },
  },
}
</script>

<style scoped lang="scss">
.documents-operations-panels {
  background: transparent !important;
}

.documents-operation-panel {
  background: #181818 !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 10px !important;
  overflow: hidden;
  transition: border-color 0.15s ease-in-out;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15) !important;
  }

  &::before {
    box-shadow: none !important;
  }

  ::v-deep .v-expansion-panel-header {
    background: #181818 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    min-height: 58px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.min-w-0 {
  min-width: 0 !important;
}

.operation-alias-text {
  font-size: 0.92rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.right-meta-col {
  white-space: nowrap;
}

.aml-chip {
  min-width: 96px;
  justify-content: center;
}

.date-col {
  min-width: 145px;
}

.zip-btn {
  font-size: 11px !important;
  letter-spacing: 0.01em;
}

.documents-tab-card {
  background: rgba(0, 0, 0, 0.25) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.documents-tab-table {
  background: transparent !important;

  ::v-deep table {
    table-layout: fixed !important;
    width: 100% !important;
  }

  ::v-deep .v-data-table__wrapper {
    overflow-x: hidden !important;
  }

  ::v-deep thead th,
  ::v-deep tbody td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }

  ::v-deep thead th:first-child,
  ::v-deep tbody td:first-child {
    padding-left: 14px !important;
  }

  ::v-deep thead th:last-child,
  ::v-deep tbody td:last-child {
    padding-right: 14px !important;
  }

  ::v-deep thead th {
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(255, 255, 255, 0.5) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
    white-space: nowrap !important;
  }
}

.empty-state-card {
  background: rgba(255, 255, 255, 0.02) !important;
  border: 1px dashed rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
}
</style>
